import { Box } from '@mui/material'
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import { AdditionalServicesPage } from '@/features/additional-services'
import { AuthGuard } from '@/features/auth'
import { BAFADetailsPage } from '@/features/bafa-details'
import { CurrentRatingPage } from '@/features/current-rating'
import { EnergyCostOverview } from '@/features/energy-cost'
import { ISFPUploadPage } from '@/features/isfp-upload'
import { MeasuresOverviewPage } from '@/features/measures-overview'
import { StartPage } from '@/features/start'
import { AssessmentGuard } from '@/shared/components/AssessmentGuard'
import BuildingUuidRequired from '@/shared/components/BuildingUuidRequired'
import { localStorage } from '@/shared/utils/localStorage'
import { sessionStorage } from '@/shared/utils/sessionStorage'

const AnimatedRoutes = () => {
  const location = useLocation()

  return (
    <TransitionGroup component={null}>
      <CSSTransition key={location.pathname} classNames="fade" timeout={300} unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            top: 0,
            left: 0,
            overflowY: 'auto',
            height: '100vh',
            '&.fade-enter': {
              opacity: 0,
              pointerEvents: 'none',
            },
            '&.fade-enter-active': {
              opacity: 1,
              transition: 'opacity 300ms ease-in-out',
            },
            '&.fade-exit': {
              opacity: 1,
              pointerEvents: 'none',
            },
            '&.fade-exit-active': {
              opacity: 0,
              transition: 'opacity 300ms ease-in-out',
            },
          }}
        >
          <Routes location={location}>
            <Route path="/" element={<BuildingUuidRequired />} />
            <Route path="/:buildingUuid/isfp-upload" element={<ISFPUploadPage />} />
            <Route
              path="/:buildingUuid"
              element={
                <AssessmentGuard>
                  <StartPage />
                </AssessmentGuard>
              }
            />
            <Route
              path="/:buildingUuid/energy-cost"
              element={
                <AssessmentGuard>
                  <EnergyCostOverview />
                </AssessmentGuard>
              }
            />
            <Route
              path="/:buildingUuid/current-rating"
              element={
                <AssessmentGuard>
                  <CurrentRatingPage />
                </AssessmentGuard>
              }
            />
            <Route
              path="/:buildingUuid/measures-overview"
              element={
                <AssessmentGuard>
                  <MeasuresOverviewPage />
                </AssessmentGuard>
              }
            />
            <Route
              path="/:buildingUuid/bafa-details"
              element={
                <AssessmentGuard>
                  <BAFADetailsPage />
                </AssessmentGuard>
              }
            />
            <Route
              path="/:buildingUuid/additional-services"
              element={
                <AssessmentGuard>
                  <AdditionalServicesPage />
                </AssessmentGuard>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </CSSTransition>
    </TransitionGroup>
  )
}

const AppRouter = () => {
  useEffect(() => {
    const isDevelopment = import.meta.env.DEV

    if (isDevelopment) {
      sessionStorage.initializeSessionFromEnv()
    } else {
      sessionStorage.initializeSessionFromQueryParams()
      localStorage.initializeFromQueryParams()
    }
  }, [])

  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <AuthGuard>
        <Box sx={{ position: 'relative', minHeight: '100vh' }}>
          <AnimatedRoutes />
        </Box>
      </AuthGuard>
    </BrowserRouter>
  )
}

export default AppRouter
